import { useEffect, useState } from 'react'
import { deleteMyAccount } from '../api/me'
import { serverErrorMessage } from '../api/client'
import './ModalBackdrop.css'
import './DeleteAccountSheet.css'

/**
 * "Delete my account", behind two confirmations.
 *
 * Its own component rather than two more modal blocks and their state inside `Settings.tsx`.
 *
 * **Step 1 names what is lost.** "This cannot be undone" tells a user nothing they can weigh; a
 * list of what actually goes does. It also says what does *not* go — a league they created keeps
 * running for its other members — because that is what people worry about. `Continue` deletes
 * nothing: it opens step 2.
 *
 * **Step 2 is the typed name.** `Delete forever` stays disabled until the input matches exactly,
 * keyed to the account rather than to a fixed magic word, so a reflexive double-tap cannot get
 * through.
 *
 * Both steps validate here, so the server's 400 is the backstop, not the UX. A 409 is rendered
 * verbatim: those messages explain what to do next (take the address out of `ADMIN_EMAILS`, sign
 * in properly rather than through the dev bypass), and "Something went wrong" throws that away.
 */

interface DeleteAccountSheetProps {
  /** The string the user must type — the same name Settings shows in the name field. */
  displayName: string
  onCancel: () => void
  /** Runs once the server has confirmed the deletion: sign out, then reload to the login screen. */
  onDeleted: () => void
}

function DeleteAccountSheet({ displayName, onCancel, onDeleted }: DeleteAccountSheetProps) {
  const [step, setStep] = useState<'warning' | 'confirm'>('warning')
  const [typed, setTyped] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Escape closes either step and leaves nothing half-done — matching every other sheet in the
  // app. Never while the request is in flight: the account may already be gone.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) onCancel()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [busy, onCancel])

  const canDelete = typed === displayName && !busy

  const remove = async () => {
    if (!canDelete) return
    setBusy(true)
    setError(null)
    try {
      await deleteMyAccount(typed)
      onDeleted()
    } catch (failure) {
      setError(serverErrorMessage(failure))
      setBusy(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={() => !busy && onCancel()}>
      <div
        className="modal-backdrop__content delete-account"
        role="dialog"
        aria-modal="true"
        aria-label="Delete account"
        onClick={(e) => e.stopPropagation()}
      >
        {step === 'warning' ? (
          <>
            <p className="delete-account__title">Delete your account?</p>
            <p className="delete-account__body">
              This is immediate and permanent. There is no undo and no grace period.
            </p>
            <p className="delete-account__body">These go, for good:</p>
            <ul className="delete-account__list">
              <li>Every prediction you have made, and the points from them</li>
              <li>Your chips and your quest picks</li>
              <li>Your whole season history</li>
              <li>Every league you have joined</li>
              <li>Your rivalries and their head-to-head records</li>
            </ul>
            <p className="delete-account__body">
              A league you created keeps running for its other members, without an owner. You can
              sign in again with the same email later — it starts a brand-new, empty account.
            </p>
            <div className="delete-account__actions">
              <button
                type="button"
                className="delete-account__btn delete-account__btn--cancel"
                onClick={onCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="delete-account__btn delete-account__btn--danger"
                onClick={() => setStep('confirm')}
              >
                Continue
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="delete-account__title">Type your name to confirm</p>
            <p className="delete-account__body">
              Type <strong>{displayName}</strong> below to delete your account and everything in it.
            </p>
            <input
              className="delete-account__input"
              value={typed}
              autoFocus
              aria-label="Type your display name to confirm"
              placeholder={displayName}
              onChange={(e) => {
                setTyped(e.target.value)
                setError(null)
              }}
            />
            {error && <p className="delete-account__error">{error}</p>}
            <div className="delete-account__actions">
              <button
                type="button"
                className="delete-account__btn delete-account__btn--cancel"
                onClick={onCancel}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="delete-account__btn delete-account__btn--danger"
                onClick={() => void remove()}
                disabled={!canDelete}
              >
                {busy ? 'Deleting…' : 'Delete forever'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default DeleteAccountSheet
